import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  MessageSquare,
  Calendar,
  MoreVertical,
  Edit2,
  Trash2,
  Pin,
  Lock,
  PinOff,
  SortDesc,
  FileText,

} from 'lucide-react';
import { forumService } from '@/services/forum.service';
import type { ForumThreadDto } from '@/types/forum.types';
import type { DiscussionSortBy } from '@/types/discussion.types'; // New import
import {
  getThreadAuthorName,
  getThreadAuthorInitials,
  formatThreadDate,
} from '@/types/forum.types';
import DiscussionList from './DiscussionList';
import ThreadForm from '../ai-learn/ThreadForm';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';

interface ThreadDetailViewProps {
  threadId: string;
  courseId: string;
  onBack: () => void;
  onThreadUpdate?: (thread: ForumThreadDto) => void;
  onThreadDelete?: (threadId: string) => void;
}

export default function ThreadDetailView({
  threadId,
  courseId,
  onBack,
  onThreadUpdate,
  onThreadDelete,
}: ThreadDetailViewProps) {
  const { user } = useAuth();
  const [thread, setThread] = useState<ForumThreadDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // New state to manage sorting, lives in the parent component now
  const [sortBy, setSortBy] = useState<DiscussionSortBy>('newest');
  
  // Sort options are also defined here now
  const sortOptions: { value: DiscussionSortBy; label: string }[] = [
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'most_liked', label: 'Most Liked' },
  ];

  const isAuthor = user?.id === thread?.created_by;
 const handleCommentPosted = () => {
    if (thread) {
      setThread({
        ...thread,
        comment_count: thread.comment_count + 1,
      });
    }
  };
  const handleCommentDeleted = () => {
    if (thread) {
      // Use Math.max to ensure the count never goes below zero
      setThread({
        ...thread,
        comment_count: Math.max(0, thread.comment_count - 1),
      });
    }
  };
  useEffect(() => { 
    loadThread();
   }, [threadId]);
  const loadThread = async () => {
     try { setLoading(true);
       const data = await forumService.getThreadById(threadId); 
       setThread(data); }
        catch (error) {
           toast.error('Failed to load thread'); 
          } 
          finally { setLoading(false); } };
     const handleEdit = () => { 
      setIsEditing(true); 
      setShowMenu(false); };
    const handleEditSuccess = (updated: ForumThreadDto) => {
     setThread(updated); 
     setIsEditing(false); 
     if (onThreadUpdate) 
      onThreadUpdate(updated); 
     toast.success('Thread updated!'); };
   const openDeleteModal = () => { 
    setShowMenu(false); 
    setDeleteModalOpen(true); };
  const confirmDeleteThread = async () => {
     if (!thread) return; 
     setDeleteLoading(true); 
     try { 
      await forumService.deleteThread(threadId);
       toast.success('Thread deleted successfully.'); 
       setDeleteModalOpen(false); 
       if (onThreadDelete) {
         onThreadDelete(threadId); 
        }
         else { onBack(); 

         } 
        }
         catch (error: any) { 
          const errorMessage = error?.response?.data?.error?.error_description || error?.response?.data?.description 
          || error?.message || 'Failed to delete thread'; 
          toast.error(errorMessage); 
        } 
          finally {
             setDeleteLoading(false); } };
      const handleTogglePin = async () => { 
      if (!thread) return;
       try { setActionLoading(true);
         const updated = await forumService.togglePinThread(threadId, !thread.is_pinned); 
         setThread(updated);
          if (onThreadUpdate) 
            onThreadUpdate(updated); 
          toast.success(updated.is_pinned ? 'Thread pinned' : 'Thread unpinned');
         } 
         catch (error: any) {
           const errorMessage = error?.response?.data?.error?.error_description || error?.response?.data?.description
            || error?.message || 'Failed to update thread'; 
            toast.error(errorMessage); 
          }
           finally { 
            setActionLoading(false);
             setShowMenu(false); 
            } 
            };
  
      if (loading) { 
      return ( <div className="h-full flex flex-col bg-white">
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <SkeletonLoader type="custom" className="h-6 w-1/2" />
            <SkeletonLoader type="custom" className="h-6 w-1/4" />
            </div>
            </div>
            <div className="flex-1 bg-gray-50 px-6 py-4 space-y-4">{Array.from({ length: 5 }).map((_, index) =>
             (<div key={index} className="flex gap-3 p-4">
              <SkeletonLoader type="avatar" /><div className="flex-1 space-y-2">
                <SkeletonLoader type="custom" className="h-4 w-32" />
                <SkeletonLoader type="text" count={2} />
                </div>
                </div>
                )
              )}
              </div>
              </div> ); 
              }
       if (!thread) { 
        return (
         <div className="flex flex-col items-center justify-center h-full bg-gray-50">
          <p className="text-gray-600 mb-4">Thread not found</p>
          <button onClick={onBack} 
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">Back to Threads</button>
          </div> 
          ); 
        }
  
      const authorName = getThreadAuthorName(thread);
      const authorInitials = getThreadAuthorInitials(thread);

     return (
      <>
      {/*
      <div className="h-full flex flex-col bg-white">
        <div className="flex-shrink-0 flex items-center justify-between gap-4 border-b px-4 py-3">
          // ... header content
          // The filter was NOT here in the previous version
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50">
          // ...
          <DiscussionList threadId={threadId} /> // Previously did not pass sortBy
        </div>
      </div>
      */}

      
      <div className="h-full flex flex-col bg-white">
        {/* Compact Header */}
        <div className="flex-shrink-0 flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
          {/* Left Side: Back Arrow and Title */}
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onBack} className="flex-shrink-0 p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" aria-label="Back to Threads">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
          <h1
          className="text-lg font-semibold text-gray-900 truncate"
          title={thread.title}
          >
          {thread.title}
        </h1>
        <p
          className="text-sm text-gray-600 truncate"
          title={thread.description}
        >
          {thread.description}
        </p>
       </div>
            {thread.is_pinned && 
            <Pin className="w-4 h-4 text-indigo-600 flex-shrink-0" />}
            {thread.is_locked && 
            <Lock className="w-4 h-4 text-red-600 flex-shrink-0" />}
          </div>

          {/* Right Side: Meta Info, NEW Filter, and Actions */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
              {thread.creator?.profile_picture ? (<img src={thread.creator.profile_picture}
               alt={authorName} 
               className="w-6 h-6 rounded-full object-cover"/>) 
               : (<div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">{authorInitials}</div>)}
              <span className="font-medium">{authorName}</span>
            </div>
            <div className="hidden lg:flex items-center gap-1.5 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>{formatThreadDate(thread.createdAt)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <MessageSquare className="w-4 h-4" />
              <span>{thread.comment_count}</span>
            </div>
            
            {/* The filter dropdown is now located here */}
            <div className="flex items-center gap-2">
              <SortDesc className="w-4 h-4 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as DiscussionSortBy)}
                 className="text-sm  border border-black rounded-lg px-2 py-1 bg-white 
                 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Actions Menu */}
            {isAuthor && (
              <div className="relative">
                <button onClick={() => setShowMenu(!showMenu)} disabled={actionLoading} 
                 className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50">
                  <MoreVertical className="w-5 h-5" />
                </button>
                {showMenu && ( <> <div className="fixed inset-0 z-10" 
                onClick={() => setShowMenu(false)}/> 
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <button onClick={handleEdit} 
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Edit2 className="w-4 h-4" />
                    <span>Edit Thread</span>
                    </button><button onClick={handleTogglePin} 
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                      {thread.is_pinned ? (
                        <>
                        <PinOff className="w-4 h-4" />
                        <span>Unpin</span></>) : (
                          <>
                          <Pin className="w-4 h-4" />
                          <span>Pin</span>
                          </>
                        )}
                        </button><hr className="my-1 border-gray-200" />
                        <button onClick={openDeleteModal} 
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Thread</span>
                          </button>
                          </div>
                          </>
                        )}
                </div>
             )}
            </div>
           </div>
        
        {isEditing && 
        (<ThreadForm courseId={courseId} thread={thread} onClose={() => setIsEditing(false)} 
        onSuccess={handleEditSuccess}/>)}
        
        {/* Discussion List Container */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {thread.is_locked ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">This thread is locked</p>
                <p className="text-sm text-gray-500">No new comments can be added</p>
                </div></div>
          ) : (
            <DiscussionList 
            threadId={threadId} 
            sortBy={sortBy} 
            onCommentPosted={handleCommentPosted} 
          onCommentDeleted={handleCommentDeleted}
            />
          )}
        </div>

        <DeleteConfirmationModal 
        isOpen={deleteModalOpen} 
        onClose={() => setDeleteModalOpen(false)}
         onConfirm={confirmDeleteThread} 
         title="Delete Thread" 
         description="Are you sure you want to delete this thread? All discussions will be permanently deleted." 
         itemName={thread?.title} 
         isLoading={deleteLoading}/>
      </div>
    </>
  );
}